/**
 * PaymentMethodCard Component
 *
 * Displays the current payment method on file - supports both cards and bank accounts.
 * Shows card brand/last4/expiry for cards, or bank name/last4 for ACH.
 * Provides an "Update" button to change payment method.
 */

import { AccountBalance, CreditCard, HourglassEmpty } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Skeleton,
  Stack,
  type SxProps,
  type Theme,
  Typography,
} from '@mui/material';
import type {
  BillingDetails,
  PaymentMethodInfo,
  PaymentMethodInfo2,
  PaymentMethodResponse,
} from '@store/generated/subscriptions';

interface PaymentMethodCardProps {
  paymentMethod: PaymentMethodResponse | PaymentMethodInfo | PaymentMethodInfo2 | null | undefined;
  billingDetails?: BillingDetails | null;
  isLoading?: boolean;
  onUpdate: () => void;
  sx?: SxProps<Theme>;
  elevation?: number;
  title?: string;
}

// Map card brand to display name
const formatCardBrand = (brand: string): string => {
  const brandMap: Record<string, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brandMap[brand.toLowerCase()] || brand.toUpperCase();
};

// Format expiration date
const formatExpiry = (month: number, year: number): string => {
  const monthStr = month.toString().padStart(2, '0');
  const yearStr = year.toString().slice(-2);
  return `${monthStr}/${yearStr}`;
};

// Format account type
const formatAccountType = (type: string): string => {
  return type.charAt(0).toUpperCase() + type.slice(1);
};

type PaymentMethodType = 'card' | 'us_bank_account' | 'none';

interface CardInfo {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface BankAccountInfo {
  bank_name: string;
  last4: string;
  account_type?: string;
}

export function PaymentMethodCard({
  paymentMethod,
  billingDetails,
  isLoading,
  onUpdate,
  sx,
  elevation = 2,
  title,
}: PaymentMethodCardProps) {
  if (isLoading) {
    return (
      <Card elevation={elevation} sx={sx}>
        {title && (
          <CardHeader title={title} slotProps={{ title: { variant: 'h6', fontWeight: 600 } }} />
        )}
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular"  sx={{ width: 48, height: 48 }}/>
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text"  sx={{ width: "50%", height: 28 }}/>
              <Skeleton variant="text"  sx={{ width: "30%", height: 20 }}/>
            </Box>
            <Skeleton variant="rectangular"  sx={{ width: 100, height: 36 }}/>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Extract payment method details, handling both nested and flat response structures
  const getPaymentMethodDetails = (): {
    type: PaymentMethodType;
    card?: CardInfo;
    bankAccount?: BankAccountInfo;
  } => {
    if (!paymentMethod) return { type: 'none' };

    // Helper to extract from a payment method object
    const extractDetails = (pm: Record<string, unknown>) => {
      if (pm.card && typeof pm.card === 'object') {
        return {
          type: 'card' as const,
          card: pm.card as CardInfo,
        };
      }
      if (pm.us_bank_account && typeof pm.us_bank_account === 'object') {
        return {
          type: 'us_bank_account' as const,
          bankAccount: pm.us_bank_account as BankAccountInfo,
        };
      }
      return { type: 'none' as const };
    };

    // PaymentMethodResponse has nested payment_method
    if ('payment_method' in paymentMethod && paymentMethod.payment_method) {
      return extractDetails(paymentMethod.payment_method as Record<string, unknown>);
    }

    // PaymentMethodInfo has card/us_bank_account directly
    return extractDetails(paymentMethod as unknown as Record<string, unknown>);
  };

  const { type, card, bankAccount } = getPaymentMethodDetails();
  const hasPaymentMethod = paymentMethod?.has_payment_method && type !== 'none';

  // Format billing address
  const formatAddress = () => {
    if (!billingDetails?.address) return null;
    const { line1, line2, city, state, postal_code, country } = billingDetails.address;
    const parts: string[] = [];
    if (line1) parts.push(line1);
    if (line2) parts.push(line2);
    const cityStateZip = [city, state, postal_code].filter(Boolean).join(', ');
    if (cityStateZip) parts.push(cityStateZip);
    if (country) parts.push(country);
    return parts;
  };

  // Check for pending micro-deposit verification
  const hasPendingVerification =
    paymentMethod && 'pending_verification' in paymentMethod && paymentMethod.pending_verification;

  // Render pending verification state
  if (hasPendingVerification) {
    return (
      <Card elevation={elevation} sx={sx}>
        {title && (
          <CardHeader title={title} slotProps={{ title: { variant: 'h6', fontWeight: 600 } }} />
        )}
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'warning.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HourglassEmpty sx={{ color: 'warning.dark' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Verification Pending
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bank account awaiting micro-deposit verification
                </Typography>
              </Box>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Two small deposits will be sent to your bank account within 1-2 business days. Once
            received, return here to verify the amounts and complete setup.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const renderIcon = () => {
    const iconProps = {
      sx: { color: hasPaymentMethod ? 'white' : 'grey.400' },
    };

    if (type === 'us_bank_account') {
      return <AccountBalance {...iconProps} />;
    }
    return <CreditCard {...iconProps} />;
  };

  const renderPaymentMethodInfo = () => {
    if (!hasPaymentMethod) {
      return (
        <>
          <Typography variant="h6" color="text.secondary">
            No Payment Method
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add a payment method to continue your subscription
          </Typography>
        </>
      );
    }

    if (type === 'card' && card) {
      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {formatCardBrand(card.brand)} ••••{card.last4}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Expires {formatExpiry(card.exp_month, card.exp_year)}
          </Typography>
        </>
      );
    }

    if (type === 'us_bank_account' && bankAccount) {
      return (
        <>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {bankAccount.bank_name} ••••{bankAccount.last4}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {bankAccount.account_type
              ? formatAccountType(bankAccount.account_type)
              : 'Bank Account'}
          </Typography>
        </>
      );
    }

    return null;
  };

  const addressLines = billingDetails ? formatAddress() : null;
  const hasBillingDetails = billingDetails && (
    billingDetails.name ||
    billingDetails.email ||
    billingDetails.phone ||
    addressLines?.length
  );

  return (
    <Card elevation={elevation} sx={sx}>
      {title && (
        <CardHeader title={title} slotProps={{ title: { variant: 'h6', fontWeight: 600 } }} />
      )}
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: hasPaymentMethod ? 'primary.main' : 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {renderIcon()}
            </Box>
            <Box>{renderPaymentMethodInfo()}</Box>
          </Box>
          <Button variant="outlined" onClick={onUpdate}>
            {hasPaymentMethod ? 'Update' : 'Add Payment Method'}
          </Button>
        </Box>

        {/* Billing Details Section */}
        {hasBillingDetails && (
          <>
            <Divider sx={{ my: 3 }} />
            <Stack spacing={1.5}>
              {billingDetails.name && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body2">{billingDetails.name}</Typography>
                </Box>
              )}
              {billingDetails.email && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2">{billingDetails.email}</Typography>
                </Box>
              )}
              {billingDetails.phone && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">{billingDetails.phone}</Typography>
                </Box>
              )}
              {addressLines && addressLines.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                  {addressLines.map((line, i) => (
                    <Typography key={i} variant="body2">
                      {line}
                    </Typography>
                  ))}
                </Box>
              )}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
